/* ============================================================================
   04 · PDP sticky rail + gallery parallax
   ----------------------------------------------------------------------------
   Pattern: a native-sticky buy rail against a gallery column whose shots drift
   at different rates, plus the add-to-bag confirmation.

   What's being decided here:
     • Parallax depth. Three layers pulled from --mo-parallax-*. Anything above
       ~0.3 and the image visibly lags the scroll, which people read as jank
       rather than depth. The far/mid/near split should be perceptible when you
       scroll slowly and invisible when you scroll fast — that's the target.
     • Whether the rail is sticky or GSAP-pinned. It's sticky. Native sticky is
       cheaper, survives viewport resize without a ScrollTrigger.refresh(), and
       can't desync from the scrollbar. Only pin when the page must stop.
     • Add-to-bag confirmation. Spring easing, ~800ms total, label swap rather
       than a spinner. This is the one place on a PDP where overshoot earns its
       keep: it's the moment the shopper is being told "yes, that worked."

   Conversion note worth flagging in review: the buy rail's contents must never
   be hidden behind an animation. Everything here starts from opacity 0 only
   because JS confirmed it can run (.mo-ready) — if GSAP fails, the rail renders
   fully visible and static. A price or ATC button that depends on an animation
   firing is a revenue bug, not a motion bug.
   ========================================================================= */

import { initMotion, refreshOnSettle, isReduced, MOTION } from '../../src/motion.js';
import { mountLabBar } from '../../src/lab.js';

const { gsap, ScrollTrigger } = initMotion();
mountLabBar({ title: '04 · PDP parallax' });
refreshOnSettle();

const shots = gsap.utils.toArray('[data-shot]');
const railItems = gsap.utils.toArray('[data-rail-item]');

function build() {
  ScrollTrigger?.getAll().forEach((t) => t.kill());
  gsap.killTweensOf([...shots, ...railItems]);

  if (isReduced()) {
    gsap.set([...shots, ...railItems], { opacity: 1, y: 0, clearProps: 'transform' });
    return;
  }

  // Rail entry — sequential, generous, because this is the content the shopper
  // came for. Entry finishes inside ~900ms so nothing blocks a fast clicker.
  gsap.set(railItems, { opacity: 0, y: MOTION.rise.sm });
  gsap.to(railItems, {
    opacity: 1,
    y: 0,
    duration: MOTION.dur.slow,
    ease: MOTION.ease.entrance,
    stagger: MOTION.stagger.base,
  });

  shots.forEach((shot) => {
    const depth = MOTION.parallax[shot.dataset.depth] ?? MOTION.parallax.mid;

    // Reveal on approach.
    gsap.fromTo(
      shot,
      { opacity: 0, scale: MOTION.scale.in },
      {
        opacity: 1,
        scale: 1,
        duration: MOTION.dur.deliberate,
        ease: MOTION.ease.entrance,
        scrollTrigger: { trigger: shot, start: 'top 90%', once: true },
      }
    );

    // Drift. Scrubbed against the element's own travel through the viewport,
    // so the offset is proportional to how much scroll it actually gets —
    // a fixed px value behaves differently on a 13" laptop and a 27" monitor.
    gsap.fromTo(
      shot,
      { yPercent: depth * 100 * 0.5 },
      {
        yPercent: -depth * 100 * 0.5,
        ease: MOTION.ease.linear,
        scrollTrigger: {
          trigger: shot,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true,
        },
      }
    );
  });
}

/* --- Swatch selection ------------------------------------------------------ */
const swatches = document.querySelector('[data-swatches]');
const colorName = document.querySelector('[data-color-name]');

swatches.addEventListener('click', (e) => {
  const button = e.target.closest('button');
  if (!button) return;

  swatches.querySelectorAll('button').forEach((b) => b.setAttribute('aria-pressed', 'false'));
  button.setAttribute('aria-pressed', 'true');

  const tone = getComputedStyle(button).backgroundColor;

  // Label cross-fades rather than snapping — a hard text swap at this size
  // reads as a glitch.
  gsap
    .timeline()
    .to(colorName, { opacity: 0, y: -4, duration: MOTION.dur.quick, ease: MOTION.ease.exit })
    .set(colorName, { textContent: button.dataset.name })
    .to(colorName, { opacity: 1, y: 0, duration: MOTION.dur.base, ease: MOTION.ease.entrance });

  // Gallery follows the colorway. With real assets this swaps image sources;
  // the timing is the part being decided, not the payload.
  gsap.to(shots, {
    duration: MOTION.dur.slow,
    ease: MOTION.ease.standard,
    stagger: MOTION.stagger.tight,
    background: `linear-gradient(160deg, ${tone}, color-mix(in srgb, ${tone} 55%, #000) 85%)`,
  });
});

/* --- Sizes ----------------------------------------------------------------- */
document.querySelector('.pdp__sizes').addEventListener('click', (e) => {
  const button = e.target.closest('button');
  if (!button) return;
  button.parentElement
    .querySelectorAll('button')
    .forEach((b) => b.setAttribute('aria-pressed', 'false'));
  button.setAttribute('aria-pressed', 'true');
});

/* --- Add to bag ------------------------------------------------------------ */
const atc = document.querySelector('[data-atc]');
const atcLabel = document.querySelector('[data-atc-label]');

atc.addEventListener('click', () => {
  if (atc.dataset.state === 'added') return;
  atc.dataset.state = 'added';

  gsap
    .timeline()
    .to(atcLabel, { yPercent: -120, opacity: 0, duration: MOTION.dur.quick, ease: MOTION.ease.exit })
    .set(atcLabel, { textContent: 'Added to bag', yPercent: 120 })
    .to(atcLabel, {
      yPercent: 0,
      opacity: 1,
      duration: MOTION.dur.slow,
      ease: MOTION.ease.spring,
    })
    // Scale pulse on the button itself. Small — 1.015, not 1.05. At full button
    // width even 2% is a lot of pixels of travel.
    .fromTo(
      atc,
      { scale: 1 },
      { scale: 1.015, duration: MOTION.dur.quick, yoyo: true, repeat: 1, ease: MOTION.ease.standard },
      '<'
    )
    .to(atcLabel, { yPercent: -120, opacity: 0, duration: MOTION.dur.quick, ease: MOTION.ease.exit }, '+=1.4')
    .set(atcLabel, { textContent: 'Add to bag', yPercent: 120 })
    .set(atc, { attr: { 'data-state': '' } })
    .to(atcLabel, { yPercent: 0, opacity: 1, duration: MOTION.dur.base });
});

build();
window.addEventListener('mo:replay', build);
